import { AxiosResponse } from 'axios';
import { observable, computed, action, toJS } from 'mobx';

import Services from '@services/index';
import Store from './Store';
import { Loading, endLoading } from '@stores/interfaces/Loading';

export interface NewsItem {
  id: number;
  title: string;
  text: string;
  date: string;
  image: string;
}

export interface NewsApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: NewsItem[];
}

class NewsStore extends Store implements Loading {
  @observable private _list: NewsItem[];
  @observable private _loading: boolean;
  @observable private _count: number;
  @observable private _hasMore: boolean;
  @observable private _page: number;

  @action private _endLoading = endLoading(200).bind(this);

  public constructor(services: Services) {
    super(services);

    this._loading = false;
    this._list = [];
    this._count = 0;
    this._hasMore = true;
    this._page = 1;
  }

  @action public fetchNews = async (params?: {
    date_start?: string;
    date_end?: string;
    limit?: number;
  }): Promise<boolean> => {
    const { date_start, date_end, limit = 10 } = params || {};

    if (this._loading || (this._page > 1 && !this._hasMore)) {
      return false;
    }

    this._loading = true;
    let success = false;

    await this._services.ossa.requests
      .news({ page: this._page, date_start, date_end, limit })
      .then(({ data }: AxiosResponse<NewsApiResponse>): void => {
        if (this._page === 1) {
          this._list = data.results;
        } else {
          this._list = [...this._list, ...data.results];
        }

        this._count = data.count;
        this._hasMore = data.next !== null;
        success = true;
      })
      .catch(error => {
        console.error('Failed to fetch news:', error);
        success = false;
      })
      .finally(() => {
        this._loading = false;
        this._endLoading();
      });

    return success;
  };

  @action public fetchNextPage = async (): Promise<void> => {
    if (this._loading || !this._hasMore) {
      return;
    }
    this._page += 1;
    await this.fetchNews();
  };

  // Метод переведен на стрелочную функцию для безопасного сохранения контекста "this"
  @action public reset = (): void => {
    this._list = [];
    this._count = 0;
    this._hasMore = true;
    this._page = 1;
    this._loading = false;
  };

  @computed public get list(): NewsItem[] {
    return toJS<NewsItem[]>(this._list);
  }

  @computed public get loading(): boolean {
    return this._loading;
  }

  @computed public get totalCount(): number {
    return this._count;
  }

  @computed public get hasMore(): boolean {
    return this._hasMore;
  }
}

export default NewsStore;
